/**
 * ArgusLiveShareDialog — Share + grant management UI for an Argus Live
 * dashboard (AL-C5).
 *
 * Implements the grant CRUD surface required by ARGUS-LIVE-SPEC §8.2.
 * Pairs with the AL-A2 endpoints:
 *   - GET    /api/v1/argus-live/dashboards/:id/grants
 *   - POST   /api/v1/argus-live/dashboards/:id/grants
 *   - DELETE /api/v1/argus-live/dashboards/:id/grants/:grantId
 *
 * UX patterned on the Library `ShareFolderDialog` so the platform feels
 * coherent. Differences:
 *
 *   - Bulk add: a single open can stage multiple users + groups + roles
 *     at once. The dialog sends one POST per grant (the API is
 *     idempotent per grantee — partial failures are visually surfaced
 *     in a per-row results column inside the toast).
 *   - Sysadmin "share with everyone in <tenant>": when the dialog is
 *     mounted inside a sysadmin context (`useAuth().isSysadmin === true`),
 *     an extra one-click affordance grants the configured role to ALL
 *     tenant roles (owner/admin/member/viewer). This is gated behind
 *     `share:argus_live` and the per-dashboard admin grant just like the
 *     normal flow — sysadmin presence does not bypass either layer, the
 *     UI just exposes the convenience expansion.
 *
 * Permission gating (two-layer per ARGUS-LIVE-SPEC §5.5):
 *
 *   - Layer 1: `share:argus_live` — comes from `useHasPermission` and is
 *     surfaced as `permissions.canShare`. The Share *button* on the host
 *     page is hidden when this is false, so by the time the dialog is
 *     mounted Layer 1 is already satisfied. The dialog re-checks for
 *     direct-URL embedding via storybook etc.
 *
 *   - Layer 2: the caller must hold an admin-class grant on this
 *     dashboard (or be the implicit owner). The mount site looks at the
 *     dashboard's `effectiveRole` and computes `canShare` via
 *     `computeAffordances`. The dialog itself uses `canMutate` to render
 *     either an editable form OR a read-only ACL viewer (the spec wants
 *     view-class+ users to see who else has access; only admin can
 *     mutate it).
 *
 * Audit emission: every grant add + remove fires through AL-A2's POST /
 * DELETE handlers, which call `appendAdminAuditEvent` with the canonical
 * `argus_live.grant.added` / `.removed` event types. The dialog does NOT
 * emit a separate audit — the endpoint is the source of truth.
 *
 * Logger: matches the post-message host pattern
 * (`console.warn(\`[argus-live:share] ...\`)`). No web-side pino sink
 * exists yet (per project memory: console-with-prefix is the current
 * convention).
 */

import { useMemo, useState } from 'react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from '@/components/ui/tabs';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Checkbox } from '@/components/ui/checkbox';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  AlertCircle,
  Info,
  Loader2,
  Plus,
  Search,
  Share2,
  ShieldCheck,
  Trash2,
  Users,
  X,
} from 'lucide-react';
import { useQuery } from '@tanstack/react-query';
import { toast } from 'sonner';
import { apiClient } from '@/lib/api-client';
import { getApiErrorMessage } from '@/lib/api-error';
import { cn } from '@/lib/utils';
import { useAuth } from '@/contexts/auth-context';
import {
  useArgusLiveGrants,
  useCreateArgusLiveGrant,
  useDeleteArgusLiveGrant,
  type ArgusLiveGrant,
} from '@/lib/use-argus-live';
import {
  ARGUS_LIVE_GRANT_ROLES,
  ARGUS_LIVE_TENANT_ROLES,
  describeGrantSubject,
  expandBulkGrantInputs,
  isGrantActive,
  normalizeExpiresAtForApi,
  validateGrantDraft,
  type ArgusLiveGrantRoleTier,
  type ArgusLiveGranteeKind,
  type ArgusLiveTenantRole,
} from '@/lib/argus-live-helpers';

// ─── Wire shapes for the user / group pickers ────────────────────────────

interface UserListItem {
  id: string;
  email: string;
  firstName: string | null;
  lastName: string | null;
}

interface GroupListItem {
  id: string;
  name: string;
  description?: string | null;
}

// ─── Component props ─────────────────────────────────────────────────────

export interface ArgusLiveShareDialogProps {
  /** Controlled open state. */
  open: boolean;
  /** onOpenChange propagated to the host (false = user closed). */
  onOpenChange: (next: boolean) => void;
  /** The dashboard being shared. */
  dashboardId: string;
  /** Title used in the dialog header. */
  dashboardTitle?: string;
  /**
   * Whether the caller can MUTATE the ACL. When false the dialog shows
   * a read-only grants list (so non-admin viewers can at least see who
   * else has access — spec §8.2). When true the full add/remove form is
   * rendered.
   */
  canMutate: boolean;
}

// ─── Component ───────────────────────────────────────────────────────────

const log = (msg: string, ctx?: unknown) =>
  console.info(`[argus-live:share] ${msg}`, ctx ?? '');

export function ArgusLiveShareDialog({
  open,
  onOpenChange,
  dashboardId,
  dashboardTitle,
  canMutate,
}: ArgusLiveShareDialogProps) {
  const auth = useAuth();
  const grantsQuery = useArgusLiveGrants(open ? dashboardId : null);
  const createGrant = useCreateArgusLiveGrant();
  const deleteGrant = useDeleteArgusLiveGrant();

  const [activeTab, setActiveTab] = useState<ArgusLiveGranteeKind>('user');
  const [selectedUserIds, setSelectedUserIds] = useState<string[]>([]);
  const [selectedGroupIds, setSelectedGroupIds] = useState<string[]>([]);
  const [selectedRoles, setSelectedRoles] = useState<ArgusLiveTenantRole[]>([]);
  const [grantRole, setGrantRole] = useState<ArgusLiveGrantRoleTier>('view');
  const [expiresAt, setExpiresAt] = useState<string>('');
  const [userSearch, setUserSearch] = useState<string>('');
  const [submitting, setSubmitting] = useState<boolean>(false);

  // ── Pickers ────────────────────────────────────────────────────────────

  // S3 P1 #8 fix: switched from `/users?pageSize=50` (admin-gated via
  // requireAdmin) to `/users/search` (any authenticated tenant member).
  // The previous endpoint 403'd for non-admin sharers — e.g. a power_user
  // who owned a dashboard and had `share:argus_live` — leaving the Users
  // tab in a forever-loading state. The new endpoint returns the same
  // minimal `{ id, email, firstName, lastName }` projection the picker
  // needs and is scoped to the caller's tenant by an INNER JOIN on
  // user_organizations.
  const usersQuery = useQuery({
    queryKey: ['users', 'search', { search: userSearch, scope: 'argus-live-share' }],
    queryFn: () =>
      apiClient.get<{ data: UserListItem[] }>(
        `/users/search?pageSize=50${userSearch ? `&search=${encodeURIComponent(userSearch)}` : ''}`,
      ),
    enabled: open && canMutate && activeTab === 'user',
  });

  const groupsQuery = useQuery({
    queryKey: ['users', 'groups', { scope: 'argus-live-share' }],
    queryFn: () =>
      apiClient.get<{ data: GroupListItem[] }>('/users/groups?pageSize=200'),
    enabled: open && canMutate && activeTab === 'group',
  });

  // ── Reset form on dialog close ──────────────────────────────────────────

  const reset = () => {
    setSelectedUserIds([]);
    setSelectedGroupIds([]);
    setSelectedRoles([]);
    setGrantRole('view');
    setExpiresAt('');
    setUserSearch('');
    setActiveTab('user');
  };

  const handleOpenChange = (next: boolean) => {
    if (!next) reset();
    onOpenChange(next);
  };

  // ── Add grant flow (bulk) ───────────────────────────────────────────────

  const totalSelected =
    selectedUserIds.length + selectedGroupIds.length + selectedRoles.length;

  const drafts = useMemo(
    () =>
      expandBulkGrantInputs({
        userIds: selectedUserIds,
        groupIds: selectedGroupIds,
        roles: selectedRoles,
        role: grantRole,
        expiresAt: normalizeExpiresAtForApi(expiresAt),
      }),
    [selectedUserIds, selectedGroupIds, selectedRoles, grantRole, expiresAt],
  );

  const validationError = useMemo(() => {
    if (drafts.length === 0) return null;
    // First-fail wins — the Add button stays disabled while any draft
    // is invalid (most commonly the expiresAt-in-past case).
    for (const d of drafts) {
      const err = validateGrantDraft(d);
      if (err) return err;
    }
    return null;
  }, [drafts]);

  const handleAdd = async () => {
    if (!canMutate) return;
    if (drafts.length === 0) {
      toast.error('Pick at least one user, group, or role.');
      return;
    }
    if (validationError) {
      toast.error(validationError);
      return;
    }
    setSubmitting(true);
    let succeeded = 0;
    let failed = 0;
    const errors: string[] = [];
    for (const d of drafts) {
      try {
        await createGrant.mutateAsync({
          dashboardId,
          granteeUserId: d.granteeUserId,
          granteeGroupId: d.granteeGroupId,
          granteeRole: d.granteeRole,
          role: d.role,
          expiresAt: d.expiresAt,
        });
        succeeded += 1;
      } catch (err) {
        failed += 1;
        const msg = getApiErrorMessage(
          err as Error & { data?: { error?: { message?: string } } },
          'Grant failed',
        );
        errors.push(msg);
        log('grant add failed', { error: msg, draft: d });
      }
    }
    setSubmitting(false);
    if (failed === 0) {
      toast.success(
        succeeded === 1
          ? 'Grant added.'
          : `Added ${succeeded} grants.`,
      );
      reset();
    } else if (succeeded === 0) {
      toast.error(`All ${failed} grants failed: ${errors[0] ?? 'see console.'}`);
    } else {
      toast.warning(
        `${succeeded} grant${succeeded === 1 ? '' : 's'} added, ${failed} failed.`,
      );
    }
  };

  const handleRemove = async (grantId: string) => {
    if (!canMutate) return;
    if (!confirm('Revoke this grant?')) return;
    try {
      await deleteGrant.mutateAsync({ dashboardId, grantId });
      toast.success('Grant revoked.');
    } catch (err) {
      const msg = getApiErrorMessage(
        err as Error & { data?: { error?: { message?: string } } },
        'Revoke failed',
      );
      log('grant revoke failed', { error: msg, grantId });
      toast.error(msg);
    }
  };

  // Sysadmin "share with everyone in tenant" — pre-fills the role tab
  // with all four tenant roles, no auto-submit (the admin still presses
  // Add). Spec §8.2 wants this behind an explicit click + confirmation.
  const handleShareWithEveryone = () => {
    if (!auth.isSysadmin || !canMutate) return;
    if (
      !confirm(
        `Stage grants for every tenant role (${ARGUS_LIVE_TENANT_ROLES.join(', ')}) at '${grantRole}'? You will still need to press Add to commit.`,
      )
    ) {
      return;
    }
    setActiveTab('role');
    setSelectedRoles([...ARGUS_LIVE_TENANT_ROLES]);
  };

  // ── Grant row helpers ───────────────────────────────────────────────────

  const grants = grantsQuery.data?.data ?? [];

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent
        className="max-w-2xl"
        data-testid="argus-live-share-dialog"
      >
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Share2 className="h-4 w-4" /> Share dashboard
          </DialogTitle>
          <DialogDescription>
            Grant view, edit, or admin access on{' '}
            <span className="font-medium">
              {dashboardTitle ?? 'this dashboard'}
            </span>
            . Grants apply only to this dashboard; the user still needs the
            <code className="font-mono text-[10px] mx-1 px-1 py-0.5 rounded bg-muted">
              read:argus_live
            </code>
            permission to see Argus Live at all.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-5 py-2">
          {!canMutate && (
            <div className="flex items-start gap-2 text-xs px-3 py-2 rounded-md bg-amber-500/10 text-amber-700 dark:text-amber-400 border border-amber-500/20">
              <AlertCircle className="h-4 w-4 shrink-0 mt-0.5" />
              <div>
                <strong>Read-only view.</strong> You can see existing grants
                but only an admin (or the owner) can change them.
              </div>
            </div>
          )}

          {canMutate && (
            <div className="flex items-start gap-2 text-xs px-3 py-2 rounded-md bg-blue-500/10 text-blue-700 dark:text-blue-400 border border-blue-500/20">
              <Info className="h-4 w-4 shrink-0 mt-0.5" />
              <div>
                <strong>Access scope.</strong> A dashboard grant is checked
                AFTER tenant + classification filters; granting{' '}
                <code className="font-mono">view</code> on a
                <code className="font-mono"> CUI/ITAR</code> dashboard still
                requires the grantee to have the matching clearance.
              </div>
            </div>
          )}

          {canMutate && (
            <div
              className="space-y-3 border rounded-md p-3 bg-muted/20"
              data-testid="argus-live-share-add"
            >
              <Tabs
                value={activeTab}
                onValueChange={(v) =>
                  setActiveTab(v as ArgusLiveGranteeKind)
                }
              >
                <TabsList className="w-full grid grid-cols-3">
                  <TabsTrigger
                    value="user"
                    data-testid="argus-live-share-tab-user"
                  >
                    Users
                  </TabsTrigger>
                  <TabsTrigger
                    value="group"
                    data-testid="argus-live-share-tab-group"
                  >
                    Groups
                  </TabsTrigger>
                  <TabsTrigger
                    value="role"
                    data-testid="argus-live-share-tab-role"
                  >
                    Tenant role
                  </TabsTrigger>
                </TabsList>

                <TabsContent value="user" className="mt-3 space-y-2">
                  <div className="relative">
                    <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-muted-foreground" />
                    <Input
                      placeholder="Search by name or email…"
                      value={userSearch}
                      onChange={(e) => setUserSearch(e.target.value)}
                      className="h-8 pl-8 text-xs"
                      aria-label="Search users"
                    />
                  </div>
                  <ScrollArea className="max-h-48 border rounded-md">
                    <div
                      className="p-1.5 space-y-1"
                      data-testid="argus-live-share-user-list"
                    >
                      {usersQuery.isLoading && (
                        <div className="flex items-center gap-2 text-xs text-muted-foreground py-2 px-2">
                          <Loader2 className="h-3.5 w-3.5 animate-spin" />
                          Loading users…
                        </div>
                      )}
                      {usersQuery.data?.data?.length === 0 && (
                        <div className="text-xs text-muted-foreground py-2 px-2">
                          No users match.
                        </div>
                      )}
                      {usersQuery.data?.data?.map((u) => {
                        const checked = selectedUserIds.includes(u.id);
                        const label =
                          [u.firstName, u.lastName].filter(Boolean).join(' ') ||
                          u.email;
                        return (
                          <label
                            key={u.id}
                            className={cn(
                              'flex items-center gap-2 px-2 py-1.5 rounded-sm hover:bg-accent cursor-pointer text-xs',
                              checked && 'bg-accent',
                            )}
                          >
                            <Checkbox
                              checked={checked}
                              onCheckedChange={(v) => {
                                setSelectedUserIds((prev) =>
                                  v
                                    ? [...prev, u.id]
                                    : prev.filter((x) => x !== u.id),
                                );
                              }}
                              data-testid={`argus-live-share-user-${u.id}`}
                            />
                            <span className="font-medium truncate">
                              {label}
                            </span>
                            {label !== u.email && (
                              <span className="text-muted-foreground truncate">
                                ({u.email})
                              </span>
                            )}
                          </label>
                        );
                      })}
                    </div>
                  </ScrollArea>
                  <div className="text-[10px] text-muted-foreground">
                    {selectedUserIds.length} selected
                  </div>
                </TabsContent>

                <TabsContent value="group" className="mt-3 space-y-2">
                  <ScrollArea className="max-h-48 border rounded-md">
                    <div
                      className="p-1.5 space-y-1"
                      data-testid="argus-live-share-group-list"
                    >
                      {groupsQuery.isLoading && (
                        <div className="flex items-center gap-2 text-xs text-muted-foreground py-2 px-2">
                          <Loader2 className="h-3.5 w-3.5 animate-spin" />
                          Loading groups…
                        </div>
                      )}
                      {groupsQuery.data?.data?.length === 0 && (
                        <div className="text-xs text-muted-foreground py-2 px-2">
                          No groups in this tenant.
                        </div>
                      )}
                      {groupsQuery.data?.data?.map((g) => {
                        const checked = selectedGroupIds.includes(g.id);
                        return (
                          <label
                            key={g.id}
                            className={cn(
                              'flex items-center gap-2 px-2 py-1.5 rounded-sm hover:bg-accent cursor-pointer text-xs',
                              checked && 'bg-accent',
                            )}
                          >
                            <Checkbox
                              checked={checked}
                              onCheckedChange={(v) => {
                                setSelectedGroupIds((prev) =>
                                  v
                                    ? [...prev, g.id]
                                    : prev.filter((x) => x !== g.id),
                                );
                              }}
                              data-testid={`argus-live-share-group-${g.id}`}
                            />
                            <Users className="h-3 w-3 text-muted-foreground" />
                            <span className="font-medium truncate">
                              {g.name}
                            </span>
                          </label>
                        );
                      })}
                    </div>
                  </ScrollArea>
                  <div className="text-[10px] text-muted-foreground">
                    {selectedGroupIds.length} selected
                  </div>
                </TabsContent>

                <TabsContent value="role" className="mt-3 space-y-2">
                  <div
                    className="space-y-1"
                    data-testid="argus-live-share-role-list"
                  >
                    {ARGUS_LIVE_TENANT_ROLES.map((r) => {
                      const checked = selectedRoles.includes(r);
                      return (
                        <label
                          key={r}
                          className={cn(
                            'flex items-center gap-2 px-2 py-1.5 rounded-sm border cursor-pointer text-xs',
                            checked && 'bg-accent border-primary/40',
                          )}
                        >
                          <Checkbox
                            checked={checked}
                            onCheckedChange={(v) => {
                              setSelectedRoles((prev) =>
                                v
                                  ? [...prev, r]
                                  : prev.filter((x) => x !== r),
                              );
                            }}
                            data-testid={`argus-live-share-role-${r}`}
                          />
                          <ShieldCheck className="h-3 w-3 text-muted-foreground" />
                          <span className="font-medium capitalize">{r}</span>
                          <span className="text-muted-foreground">
                            — everyone with the{' '}
                            <code className="font-mono">{r}</code> role in this
                            tenant.
                          </span>
                        </label>
                      );
                    })}
                  </div>

                  {auth.isSysadmin && (
                    <Button
                      type="button"
                      variant="outline"
                      size="sm"
                      className="text-xs h-7 mt-1"
                      onClick={handleShareWithEveryone}
                      data-testid="argus-live-share-everyone"
                    >
                      <Users className="h-3 w-3 mr-1.5" />
                      Stage all roles (sysadmin)
                    </Button>
                  )}
                </TabsContent>
              </Tabs>

              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-1">
                  <Label
                    htmlFor="argus-live-share-role"
                    className="text-xs"
                  >
                    Access level
                  </Label>
                  <Select
                    value={grantRole}
                    onValueChange={(v) =>
                      setGrantRole(v as ArgusLiveGrantRoleTier)
                    }
                  >
                    <SelectTrigger
                      id="argus-live-share-role"
                      className="h-8 text-xs"
                    >
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {ARGUS_LIVE_GRANT_ROLES.map((r) => (
                        <SelectItem
                          key={r}
                          value={r}
                          className="text-xs"
                        >
                          {r}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-1">
                  <Label
                    htmlFor="argus-live-share-expiry"
                    className="text-xs"
                  >
                    Expires (optional)
                  </Label>
                  <Input
                    id="argus-live-share-expiry"
                    type="datetime-local"
                    value={expiresAt}
                    onChange={(e) => setExpiresAt(e.target.value)}
                    className="h-8 text-xs"
                  />
                </div>
              </div>

              {validationError && (
                <div className="text-[11px] text-destructive flex items-start gap-1">
                  <AlertCircle className="h-3.5 w-3.5 shrink-0 mt-0.5" />
                  <span>{validationError}</span>
                </div>
              )}

              <div className="flex items-center justify-between">
                <div className="text-[11px] text-muted-foreground">
                  {totalSelected === 0
                    ? 'No grantees selected.'
                    : `Will add ${totalSelected} grant${totalSelected === 1 ? '' : 's'}.`}
                </div>
                <Button
                  size="sm"
                  disabled={
                    submitting ||
                    totalSelected === 0 ||
                    validationError !== null
                  }
                  onClick={() => void handleAdd()}
                  data-testid="argus-live-share-add-submit"
                >
                  {submitting ? (
                    <Loader2 className="h-3.5 w-3.5 mr-1.5 animate-spin" />
                  ) : (
                    <Plus className="h-3.5 w-3.5 mr-1.5" />
                  )}
                  Add
                </Button>
              </div>
            </div>
          )}

          {/* Existing grants list */}
          <div>
            <div className="flex items-center justify-between mb-2">
              <h3 className="text-sm font-medium">Current grants</h3>
              <span className="text-xs text-muted-foreground">
                {grants.length} grant{grants.length === 1 ? '' : 's'}
              </span>
            </div>
            {grantsQuery.isLoading && (
              <div className="flex items-center gap-2 text-sm text-muted-foreground py-4 px-2">
                <Loader2 className="h-3.5 w-3.5 animate-spin" /> Loading…
              </div>
            )}
            {grantsQuery.isError && (
              <div className="text-sm text-destructive py-4 px-2">
                Failed to load grants:{' '}
                {getApiErrorMessage(
                  grantsQuery.error as Error & {
                    data?: { error?: { message?: string } };
                  },
                  'Unknown error',
                )}
              </div>
            )}
            {!grantsQuery.isLoading && !grantsQuery.isError && grants.length === 0 && (
              <div className="text-sm text-muted-foreground py-4 px-2 flex items-center gap-2">
                <Info className="h-3.5 w-3.5" />
                No grants yet. Only the owner can see this dashboard.
              </div>
            )}
            <ScrollArea className="max-h-72">
              <div
                className="space-y-1.5"
                data-testid="argus-live-share-grant-list"
              >
                {grants.map((g) => (
                  <GrantRow
                    key={g.id}
                    grant={g}
                    canMutate={canMutate}
                    onRemove={() => void handleRemove(g.id)}
                    removing={deleteGrant.isPending}
                  />
                ))}
              </div>
            </ScrollArea>
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => handleOpenChange(false)}>
            <X className="h-3.5 w-3.5 mr-2" /> Close
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// ─── Grant row ──────────────────────────────────────────────────────────

interface GrantRowProps {
  grant: ArgusLiveGrant;
  canMutate: boolean;
  removing: boolean;
  onRemove: () => void;
}

function GrantRow({ grant, canMutate, removing, onRemove }: GrantRowProps) {
  // The picker queries are tab-scoped and only fetch a small window;
  // we rely on lazy resolution to surface the human name when the
  // current picker happens to include the user/group. Otherwise the
  // raw UUID is shown — it's a permission audit surface, not a chat UX,
  // so showing the canonical id is acceptable per ShareFolderDialog
  // precedent.
  const subject = describeGrantSubject(grant);
  const active = isGrantActive(grant);

  return (
    <div
      className={cn(
        'flex items-center justify-between gap-2 px-3 py-2 border rounded-md text-sm',
        !active && 'opacity-60',
      )}
      data-testid={`argus-live-share-grant-row-${grant.id}`}
    >
      <div className="flex-1 min-w-0">
        <div className="font-medium truncate" title={subject}>
          {subject}
        </div>
        <div className="flex items-center gap-1.5 mt-0.5 flex-wrap">
          <Badge variant="outline" className="text-[10px]">
            {grant.role}
          </Badge>
          {grant.granteeUserId && (
            <Badge variant="outline" className="text-[9px]">
              user
            </Badge>
          )}
          {grant.granteeGroupId && (
            <Badge variant="outline" className="text-[9px]">
              group
            </Badge>
          )}
          {grant.granteeRole && (
            <Badge variant="outline" className="text-[9px]">
              role
            </Badge>
          )}
          {grant.expiresAt && (
            <Badge
              variant="outline"
              className={cn(
                'text-[9px]',
                !active && 'border-destructive/40 text-destructive',
              )}
            >
              {active ? 'expires ' : 'expired '}
              {new Date(grant.expiresAt).toLocaleDateString()}
            </Badge>
          )}
        </div>
      </div>
      {canMutate && (
        <Button
          variant="ghost"
          size="icon"
          className="h-7 w-7"
          title="Revoke grant"
          disabled={removing}
          onClick={onRemove}
          data-testid={`argus-live-share-grant-revoke-${grant.id}`}
        >
          {removing ? (
            <Loader2 className="h-3.5 w-3.5 animate-spin" />
          ) : (
            <Trash2 className="h-3.5 w-3.5" />
          )}
        </Button>
      )}
    </div>
  );
}
